import { useEffect, useSyncExternalStore } from "react";
import { TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Panelin onay penceresi (tarayıcının gri confirm kutusu yerine). Her yerden çağrılır:
 *   if (!(await confirmAction("Görsel silinsin mi?"))) return;
 * Silme / kaldırma / kapatma gibi geri alınması zor işlemlerde onay düğmesi kırmızıdır.
 */
interface Request {
  message: string;
  title: string;
  confirmLabel: string;
  danger: boolean;
  resolve: (ok: boolean) => void;
}

let current: Request | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

const DANGER = /silin|silme|kaldır|kapatıl|ayrıl|iptal|yenilensin|değiştir/i;

export function confirmAction(message: string, opts: { title?: string; confirmLabel?: string; danger?: boolean } = {}): Promise<boolean> {
  current?.resolve(false);
  return new Promise((resolve) => {
    const danger = opts.danger ?? DANGER.test(message);
    current = { message, title: opts.title ?? (danger ? "Emin misiniz?" : "Onaylıyor musunuz?"), confirmLabel: opts.confirmLabel ?? (danger ? "Evet, devam et" : "Onayla"), danger, resolve };
    emit();
  });
}

function close(ok: boolean) {
  const r = current;
  current = null;
  emit();
  r?.resolve(ok);
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** Uygulamada bir kez yerleştirilir (main.tsx). */
export function ConfirmHost() {
  const req = useSyncExternalStore(subscribe, () => current);
  useEffect(() => {
    if (!req) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [req]);
  if (!req) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby="confirm-body">
      <button type="button" className="absolute inset-0 bg-black/45" aria-label="Vazgeç" onClick={() => close(false)} />
      <div className="relative w-full max-w-md rounded-t-2xl border bg-card p-5 shadow-2xl sm:rounded-2xl">
        <div className="flex gap-3">
          <span className={cn("mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full", req.danger ? "bg-danger/12 text-danger" : "bg-accent/12 text-accent")}>
            <TriangleAlert className="size-5" aria-hidden />
          </span>
          <div className="min-w-0 space-y-1.5">
            <h2 id="confirm-title" className="text-base font-semibold">
              {req.title}
            </h2>
            <p id="confirm-body" className="text-sm leading-relaxed whitespace-pre-line text-muted-foreground">
              {req.message}
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => close(false)}>
            Vazgeç
          </Button>
          <Button autoFocus variant={req.danger ? "danger" : "default"} onClick={() => close(true)}>
            {req.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
